/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import {
  createAction,
  deleteRequest,
  getRequest,
  putRequest,
  postRequest,
  startLoading,
  stopLoading,
  getCSV,
  escapeFilterValue
} from "openstack-uicore-foundation/lib/utils/actions";
import i18n from "i18next";
import pLimit from "p-limit";
import { getAccessTokenSafely } from "../utils/methods";
import {
  DEFAULT_PER_PAGE,
  MAX_PER_PAGE,
  PROMISE_BATCH_LIMIT
} from "../utils/constants";
import {
  ignoreErrorHandler,
  setSnackbarMessage,
  snackbarErrorHandler,
  snackbarSuccessHandler
} from "./base-actions";

export const RECEIVE_SPONSOR_DEADLINES = "RECEIVE_SPONSOR_DEADLINES";
export const REQUEST_SPONSOR_FORMS = "REQUEST_SPONSOR_FORMS";
export const RECEIVE_SPONSOR_FORMS = "RECEIVE_SPONSOR_FORMS";
export const RECEIVE_SPONSOR_FORMS_APPEND = "RECEIVE_SPONSOR_FORMS_APPEND";
export const REQUEST_SPONSOR_FORM = "REQUEST_SPONSOR_FORM";
export const RECEIVE_SPONSOR_FORM = "RECEIVE_SPONSOR_FORM";
export const REQUEST_SPONSOR_ORDERS = "REQUEST_SPONSOR_ORDERS";
export const RECEIVE_SPONSOR_ORDERS = "RECEIVE_SPONSOR_ORDERS";
export const REQUEST_SPONSOR_ORDER = "REQUEST_SPONSOR_ORDER";
export const RECEIVE_SPONSOR_ORDER = "RECEIVE_SPONSOR_ORDER";
export const CLEAR_SPONSOR_ORDER = "CLEAR_SPONSOR_ORDER";
export const RECEIVE_AVAILABLE_PAGES = "RECEIVE_AVAILABLE_PAGES";
export const AVAILABLE_PAGE_RECEIVED = "AVAILABLE_PAGE_RECEIVED";
export const MEDIA_FILE_UPLOADED = "MEDIA_FILE_UPLOADED";
export const MEDIA_FILE_DELETED = "MEDIA_FILE_DELETED";
export const MEDIA_INFO_UPDATED = "MEDIA_INFO_UPDATED";

export const REQUEST_BADGE_SCANS = "REQUEST_BADGE_SCANS";
export const RECEIVE_BADGE_SCANS = "RECEIVE_BADGE_SCANS";
export const RECEIVE_BADGE_SCAN = "RECEIVE_BADGE_SCAN";
export const BADGE_SCAN_UPDATED = "BADGE_SCAN_UPDATED";
export const RESET_BADGE_SCAN_FORM = "RESET_BADGE_SCAN_FORM";

export const RECEIVE_BADGE_SCAN_EXTRA_QUESTIONS =
  "RECEIVE_BADGE_SCAN_EXTRA_QUESTIONS";
export const RECEIVE_BADGE_SCAN_EXTRA_QUESTIONS_META =
  "RECEIVE_BADGE_SCAN_EXTRA_QUESTIONS_META";
export const RECEIVE_BADGE_SCAN_EXTRA_QUESTION =
  "RECEIVE_BADGE_SCAN_EXTRA_QUESTION";
export const BADGE_SCAN_EXTRA_QUESTION_ORDER_UPDATED =
  "BADGE_SCAN_EXTRA_QUESTION_ORDER_UPDATED";
export const BADGE_SCAN_EXTRA_QUESTION_ADDED =
  "BADGE_SCAN_EXTRA_QUESTION_ADDED";
export const BADGE_SCAN_EXTRA_QUESTION_UPDATED =
  "BADGE_SCAN_EXTRA_QUESTION_UPDATED";
export const BADGE_SCAN_EXTRA_QUESTION_DELETED =
  "BADGE_SCAN_EXTRA_QUESTION_DELETED";
export const RESET_BADGE_SCAN_EXTRA_QUESTION_FORM =
  "RESET_BADGE_SCAN_EXTRA_QUESTION_FORM";
export const BADGE_SCAN_EXTRA_QUESTION_VALUE_ADDED =
  "BADGE_SCAN_EXTRA_QUESTION_VALUE_ADDED";
export const BADGE_SCAN_EXTRA_QUESTION_VALUE_UPDATED =
  "BADGE_SCAN_EXTRA_QUESTION_VALUE_UPDATED";
export const BADGE_SCAN_EXTRA_QUESTION_VALUE_DELETED =
  "BADGE_SCAN_EXTRA_QUESTION_VALUE_DELETED";
export const BADGE_SCAN_EXTRA_QUESTION_VALUE_ORDER_UPDATED =
  "BADGE_SCAN_EXTRA_QUESTION_VALUE_ORDER_UPDATED";

/* ********************************************************************** */
/*    DASHBOARD   */
/* ********************************************************************** */

export const getSponsorDeadlines = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand: "media_upload,file_type",
    per_page: MAX_PER_PAGE
  };

  return getRequest(
    null,
    createAction(RECEIVE_SPONSOR_DEADLINES),
    `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/media-request-modules/deadlines`,
    snackbarErrorHandler
  )(params)(dispatch)
    .catch(console.log)
    .finally(() => {
      dispatch(stopLoading());
    });
};

/* ********************************************************************** */
/*    FORMS   */
/* ********************************************************************** */

export const getAllSponsorForms = () => async (dispatch, getState) => {
  const { showAccessState, sponsorFormsListState } = getState();
  const { showAccess } = showAccessState;
  const { perPage } = sponsorFormsListState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    page: 1,
    per_page: perPage,
    access_token: accessToken,
    order: "-status,-id",
    expand: "allowed_add_ons"
  };

  const url = `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-forms`;

  return getRequest(
    null,
    createAction(RECEIVE_SPONSOR_FORMS),
    url,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(({ response }) => {
      const { last_page: lastPage } = response;
      const limit = pLimit(PROMISE_BATCH_LIMIT);

      // Sum 1 to lastPage to avoid last request to be missing.
      // eslint-disable-next-line no-magic-numbers
      const requestsList = Array.from({ length: lastPage - 1 }, (_, i) =>
        getRequest(
          null,
          createAction("DUMMY_ACTION"),
          url,
          ignoreErrorHandler
          // eslint-disable-next-line no-magic-numbers
        )({ ...params, page: i + 2 })
      );

      if (!requestsList.length) return null;

      return Promise.all(
        requestsList.map((request) =>
          request ? limit(() => request(dispatch)) : Promise.resolve({})
        )
      ).then((res) => {
        const sortedFlatResponse = res
          .sort((a, b) => a.response.current_page - b.response.current_page)
          .map((r) => r.response.data)
          .flat();

        dispatch(
          createAction(RECEIVE_SPONSOR_FORMS_APPEND)(sortedFlatResponse)
        );
      });
    })
    .catch(() => {
      setSnackbarMessage({
        html: i18n.t("my_forms.load_all_error"),
        type: "error"
      })(dispatch);
    })
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const getSponsorForm =
  (formId, addonId) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken,
      expand:
        "items,items.meta_fields,items.meta_fields.values,items.images,items.type"
    };

    return getRequest(
      createAction(REQUEST_SPONSOR_FORM),
      createAction(RECEIVE_SPONSOR_FORM),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-forms/${formId}/add-ons/${addonId}`,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(({ response }) => response)
      .catch(console.log)
      .finally(() => {
        dispatch(stopLoading());
      });
  };

/* ********************************************************************** */
/*    ORDERS   */
/* ********************************************************************** */

export const getSponsorOrders =
  (page = 1, perPage = DEFAULT_PER_PAGE) =>
  async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      page,
      per_page: perPage,
      access_token: accessToken,
      order: "-id",
      "filter[]": ["status==Paid,status==Pending"]
    };

    return getRequest(
      createAction(REQUEST_SPONSOR_ORDERS),
      createAction(RECEIVE_SPONSOR_ORDERS),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/purchases`,
      snackbarErrorHandler
    )(params)(dispatch)
      .catch(console.log)
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const changePurchasePaymentMethod =
  (purchaseId) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;

    dispatch(startLoading());

    const accessToken = await getAccessTokenSafely();

    const params = {
      access_token: accessToken
    };

    // rejects on error (e.g. 412) so the caller only navigates on success
    return putRequest(
      null,
      createAction("DUMMY_ACTION"),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/purchases/${purchaseId}/change-payment-method`,
      {},
      snackbarErrorHandler
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const getSponsorOrder = (orderId) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "forms,forms.items,forms.items.meta_fields,forms.items.type,forms.items.cancellations,refunds,payments,notes,fees"
  };

  return getRequest(
    createAction(REQUEST_SPONSOR_ORDER),
    createAction(RECEIVE_SPONSOR_ORDER),
    `${window.PURCHASES_API_URL}/api/v2/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/purchases/${orderId}`,
    ignoreErrorHandler
  )(params)(dispatch)
    .then((payload) => payload.response)
    .catch((e) => {
      throw e;
    })
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const clearSponsorOrder = () => (dispatch) => {
  dispatch(createAction(CLEAR_SPONSOR_ORDER));
};

/* ********************************************************************** */
/*    PAGES   */
/* ********************************************************************** */

export const getAvailablePages = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    per_page: MAX_PER_PAGE
  };

  return getRequest(
    null,
    createAction(RECEIVE_AVAILABLE_PAGES),
    `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-pages`,
    snackbarErrorHandler
  )(params)(dispatch)
    .catch(console.log)
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const getAvailablePage = (pageId) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand: "modules,modules.file_type,modules.media_upload"
  };

  return getRequest(
    null,
    createAction(AVAILABLE_PAGE_RECEIVED),
    `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-pages/${pageId}`,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(({ response }) => response)
    .catch(console.log)
    .finally(() => {
      dispatch(stopLoading());
    });
};

export const uploadFileForMediaModule =
  (pageId, moduleId, fileObj) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      putRequest(
        null,
        createAction("DUMMY_ACTION"),
        `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-pages/${pageId}/modules/${moduleId}/file`,
        fileObj,
        snackbarErrorHandler
      )(params)(dispatch)
        .then(({ response }) => {
          dispatch(
            snackbarSuccessHandler({
              title: i18n.t("general.success"),
              html: i18n.t("my_pages.file_uploaded_success")
            })
          );
          dispatch(
            createAction(MEDIA_FILE_UPLOADED)({ ...response, moduleId })
          );
        })
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

export const removeFileForMediaModule =
  (pageId, moduleId) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      deleteRequest(
        null,
        createAction(MEDIA_FILE_DELETED)({ moduleId }),
        `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-pages/${pageId}/modules/${moduleId}/file`,
        null,
        snackbarErrorHandler
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: i18n.t("general.success"),
              html: i18n.t("my_pages.file_removed_success")
            })
          );
        })
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

export const saveInfoForMediaModule =
  (pageId, moduleId, info) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      putRequest(
        null,
        createAction(MEDIA_INFO_UPDATED),
        `${window.SPONSOR_PAGES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/available-pages/${pageId}/modules/${moduleId}/text`,
        { value: info },
        snackbarErrorHandler
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: i18n.t("general.success"),
              html: i18n.t("my_pages.info_update_success")
            })
          );
        })
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

/* ********************************************************************** */
/*    BADGE SCANS   */
/* ********************************************************************** */

export const getBadgeScans =
  (
    term = "",
    page = 1,
    perPage = DEFAULT_PER_PAGE,
    order = "attendee_last_name",
    orderDir = 1
  ) =>
  async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();
    const filter = [];
    const summitTZ = showAccess.summit.time_zone_id;

    dispatch(startLoading());

    filter.push(`sponsor_id==${showAccess.sponsor.id}`);

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(
        `attendee_first_name=@${escapedTerm},attendee_last_name=@${escapedTerm},attendee_email=@${escapedTerm},attendee_company=@${escapedTerm}`
      );
    }

    const params = {
      access_token: accessToken,
      page,
      per_page: perPage,
      expand:
        "badge,badge.ticket,badge.ticket.owner,badge.ticket.owner.member,scanned_by",
      relations:
        "scanned_by,badge,scanned_by.none,badge.none,badge.ticket.none,badge.ticket.owner.none,badge.ticket.owner.member.none",
      fields:
        "id,created,scan_date,attendee_first_name,attendee_last_name,attendee_email,attendee_company,scanned_by.email,scanned_by.first_name,scanned_by.last_name,badge.ticket,badge.ticket.owner,badge.ticket.owner.member.first_name,badge.ticket.owner.member.last_name,badge.ticket.owner.email,badge.ticket.owner.company,badge.ticket.owner.first_name,badge.ticket.owner.last_name"
    };

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "+" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return (
      getRequest(
        createAction(REQUEST_BADGE_SCANS),
        createAction(RECEIVE_BADGE_SCANS),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/badge-scans`,
        snackbarErrorHandler,
        { term, page, perPage, order, orderDir, summitTZ }
      )(params)(dispatch)
        // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

export const exportBadgeScans =
  (order = "attendee_last_name", orderDir = 1) =>
  async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();
    const filter = [];
    const filename = `${showAccess.sponsor.company_name}-BadgeScans.csv`;
    const params = {
      access_token: accessToken,
      columns:
        "scan_date,scanned_by,attendee_first_name,attendee_last_name,attendee_email,attendee_company"
    };

    filter.push(`sponsor_id==${showAccess.sponsor.id}`);

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "+" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    dispatch(
      getCSV(
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/badge-scans/csv`,
        params,
        filename
      )
    );
  };

export const getBadgeScan = (scanId) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "scanned_by,badge,badge.ticket,badge.ticket.owner,badge.ticket.owner.member,sponsor,sponsor.extra_questions,sponsor.extra_questions.values,extra_questions",
    relations:
      "scanned_by,sponsor,sponsor.none,badge,scanned_by.none,badge.none,badge.ticket.none,badge.ticket.owner.none,badge.ticket.owner.member.none",
    fields:
      "id,notes,extra_questions,created,scan_date,attendee_first_name,attendee_last_name,attendee_email,attendee_company,sponsor.id,sponsor.extra_questions,scanned_by.email,scanned_by.first_name,scanned_by.last_name,badge.ticket,badge.ticket.owner,badge.ticket.owner.member.first_name,badge.ticket.owner.member.last_name,badge.ticket.owner.email,badge.ticket.owner.company,badge.ticket.owner.first_name,badge.ticket.owner.last_name"
  };

  return (
    getRequest(
      null,
      createAction(RECEIVE_BADGE_SCAN),
      `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/badge-scans/${scanId}`,
      snackbarErrorHandler
    )(params)(dispatch)
      // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const saveBadgeScan = (entity) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const normalizedEntity = normalizeBadgeScan(entity);

  return putRequest(
    null,
    createAction(BADGE_SCAN_UPDATED),
    `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/badge-scans/${entity.id}`,
    normalizedEntity,
    snackbarErrorHandler,
    entity
  )(params)(dispatch)
    .then(() => {
      dispatch(
        snackbarSuccessHandler({
          title: i18n.t("general.success"),
          html: i18n.t("edit_badge_scan.badge_scan_saved")
        })
      );
    })
    .finally(() => dispatch(stopLoading()));
};

export const resetBadgeScanForm = () => (dispatch) => {
  dispatch(createAction(RESET_BADGE_SCAN_FORM)({}));
};

const normalizeBadgeScan = (entity) => {
  const normalizedEntity = { ...entity };

  delete normalizedEntity.sponsor_extra_questions;
  delete normalizedEntity.attendee_company;
  delete normalizedEntity.attendee_full_name;

  return normalizedEntity;
};

/* ********************************************************************** */
/*    BADGE SCANS EXTRA QUESTIONS   */
/* ********************************************************************** */

export const getBadgeScanExtraQuestions =
  (summitId, sponsorId) => async (dispatch) => {
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken,
      expand: "extra_questions,none",
      fields:
        "extra_questions.id,extra_questions.order,extra_questions.type,extra_questions.label,extra_questions.name,none",
      relations: "extra_questions"
    };

    return getRequest(
      null,
      createAction(RECEIVE_BADGE_SCAN_EXTRA_QUESTIONS),
      `${window.API_BASE_URL}/api/v2/summits/${summitId}/sponsors/${sponsorId}`,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(({ response }) => response)
      .catch(console.log)
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const getBadgeScanExtraQuestionMeta =
  () => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      getRequest(
        null,
        createAction(RECEIVE_BADGE_SCAN_EXTRA_QUESTIONS_META),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/all/extra-questions/metadata`,
        snackbarErrorHandler
      )(params)(dispatch)
        // Swallow the request util's trailing rejection (error UX already ran
        // in snackbarErrorHandler). Without this, fire-and-forget callers
        // surface it as an unhandled promise rejection.
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

export const saveBadgeScanExtraQuestion =
  (entity) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    const params = {
      access_token: accessToken
    };

    dispatch(startLoading());

    // force mandatory false in certain cases
    if (
      entity.type === "CheckBox" ||
      (entity.type === "CheckBoxList" && entity?.values?.length <= 1)
    ) {
      entity.mandatory = false;
    }

    if (entity.id) {
      return putRequest(
        null,
        createAction(BADGE_SCAN_EXTRA_QUESTION_UPDATED),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${entity.id}`,
        entity,
        snackbarErrorHandler,
        entity
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: i18n.t("general.done"),
              html: i18n.t(
                "edit_badge_scan_extra_question.extra_question_saved"
              )
            })
          );
        })
        .finally(() => dispatch(stopLoading()));
    }

    return postRequest(
      null,
      createAction(BADGE_SCAN_EXTRA_QUESTION_ADDED),
      `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions`,
      entity,
      snackbarErrorHandler,
      entity
    )(params)(dispatch)
      .then(({ response }) => {
        dispatch(
          snackbarSuccessHandler({
            title: i18n.t("general.done"),
            html: i18n.t(
              "edit_badge_scan_extra_question.extra_question_created"
            )
          })
        );
        return response;
      })
      .finally(() => dispatch(stopLoading()));
  };

export const getBadgeScanExtraQuestion =
  (extraQuestionId) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    const params = {
      access_token: accessToken,
      expand: "values"
    };

    dispatch(startLoading());

    return (
      getRequest(
        null,
        createAction(RECEIVE_BADGE_SCAN_EXTRA_QUESTION),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${extraQuestionId}`,
        snackbarErrorHandler
      )(params)(dispatch)
        // Swallow the request util's trailing rejection (error UX already ran
        // in snackbarErrorHandler). Without this, fire-and-forget callers
        // surface it as an unhandled promise rejection.
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

export const updateBadgeScanExtraQuestionOrder =
  (extraQuestions, questionId, newOrder) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return putRequest(
      null,
      createAction(BADGE_SCAN_EXTRA_QUESTION_ORDER_UPDATED)(extraQuestions),
      `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${questionId}`,
      { order: newOrder },
      snackbarErrorHandler
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const deleteBadgeScanExtraQuestion =
  (questionId) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      deleteRequest(
        null,
        createAction(BADGE_SCAN_EXTRA_QUESTION_DELETED)({ questionId }),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${questionId}`,
        null,
        snackbarErrorHandler
      )(params)(dispatch)
        .then(() => {
          dispatch(
            snackbarSuccessHandler({
              title: i18n.t("general.done"),
              html: i18n.t("badge_scans_extra_questions.extra_question_deleted")
            })
          );
        })
        // Swallow the request util's trailing rejection on failure (error UX
        // already ran in snackbarErrorHandler). Without this, fire-and-forget
        // callers surface it as an unhandled promise rejection.
        .catch(() => {})
        .finally(() => dispatch(stopLoading()))
    );
  };

export const resetSponsorExtraQuestionForm = () => (dispatch) => {
  dispatch(createAction(RESET_BADGE_SCAN_EXTRA_QUESTION_FORM)({}));
};

export const saveSponsorExtraQuestionValue =
  (questionId, entity) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    if (entity.id) {
      return putRequest(
        null,
        createAction(BADGE_SCAN_EXTRA_QUESTION_VALUE_UPDATED),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${questionId}/values/${entity.id}`,
        entity,
        snackbarErrorHandler,
        entity
      )(params)(dispatch).finally(() => {
        dispatch(stopLoading());
      });
    }

    return postRequest(
      null,
      createAction(BADGE_SCAN_EXTRA_QUESTION_VALUE_ADDED),
      `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${questionId}/values`,
      entity,
      snackbarErrorHandler,
      entity
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

/**
 * @param valueId
 * @param newOrder
 * @returns {function(*=, *): *}
 */
export const updateSponsorExtraQuestionValueOrder =
  (valueId, newOrder) => async (dispatch, getState) => {
    const { showAccessState, sponsorBadgeScansExtraQuestionState } = getState();
    const { showAccess } = showAccessState;
    const {
      entity: { id: questionId }
    } = sponsorBadgeScansExtraQuestionState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      putRequest(
        null,
        createAction(BADGE_SCAN_EXTRA_QUESTION_VALUE_ORDER_UPDATED)({
          id: valueId,
          order: newOrder
        }),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${questionId}/values/${valueId}`,
        { order: newOrder },
        snackbarErrorHandler
      )(params)(dispatch)
        // Swallow the request util's trailing rejection (error UX already ran
        // in snackbarErrorHandler). Without this, fire-and-forget callers
        // surface it as an unhandled promise rejection.
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };

export const deleteSponsorExtraQuestionValue =
  (questionId, valueId) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    return (
      deleteRequest(
        null,
        createAction(BADGE_SCAN_EXTRA_QUESTION_VALUE_DELETED)({ valueId }),
        `${window.API_BASE_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/extra-questions/${questionId}/values/${valueId}`,
        null,
        snackbarErrorHandler
      )(params)(dispatch)
        // Swallow the request util's trailing rejection (error UX already ran
        // in snackbarErrorHandler). Without this, fire-and-forget callers
        // surface it as an unhandled promise rejection.
        .catch(() => {})
        .finally(() => {
          dispatch(stopLoading());
        })
    );
  };
